import {
  Badge,
  Box,
  Button,
  Container,
  List,
  Paper,
  Typography,
} from "@mui/material";
import React, { FC, useEffect, useState } from "react";
import Organizations from "../../api/organizations";
import PATHS from "../../components/navigation/_paths";
//import { useNavigate } from "react-router";
import {
  AccountBalanceWallet,
  ArrowForwardIos,
  ContactPhone,
  CorporateFareRounded,
  Settings,
} from "@mui/icons-material";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import AddressAPI from "../../api/address";
import {
  PageContainer,
  PageContent,
  PageHeader,
} from "../../components/layout/page";
import { useGlobalOrganizationContext } from "../../hooks/useGlobalOrganizationContext";
import { useAlert } from "../../lib/alert";
import eventEmitter from "../../lib/event";
import { Organization } from "../../model";
import { OrganizationAddressLabels } from "../../model/Address";
import NotFound from "../app/NotFound";
import {
  OrganizationContextProps,
  UpdateOrganizationContext,
} from "./components/UpdateOrganizationContext";
//import { useAlert } from "../../lib/alert";
//import NotFound from "../app/NotFound";
import type { Theme } from "@mui/material/styles";

interface MenuButtonProps {
  children: React.ReactNode;
  page: string;
  clickEvent?: (num: number) => void;
}
const MenuButton: FC<MenuButtonProps & React.ComponentProps<typeof Button>> = ({
  children,
  page,
  ...props
}) => {
  const buttonStyles = (theme: Theme) => ({
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    textTransform: "none",
    fontSize: "1rem",
    fontWeight: 500,
    width: "100%",
    px: 2.5,
    py: 2,
    my: 1.5,
    borderRadius: "12px",
    border: "none",
    transition: "all 0.2s ease-in-out",
    color: theme.palette.grey[900],
    "&:hover": {
      background: theme.palette.grey[100],
      color: theme.palette.primary.main,
    },
  });
  const [isActive, setIsActive] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setIsActive(location.pathname.includes(page));
  }, [location, page]);

  return (
    <Link to={page} style={{ textDecoration: "none", color: "inherit" }}>
      <Button
        fullWidth
        startIcon={props.startIcon}
        endIcon={<ArrowForwardIos sx={{ fontSize: 16, opacity: 0.5 }} />}
        color="inherit"
        sx={
          isActive
            ? (theme) => ({
                ...buttonStyles(theme),
                background: theme.palette.action.selected,
                color: theme.palette.primary.main,
                boxShadow: `0px 0px 4px ${theme.palette.grey[400]}`,
                transform: `scale(1.01)`,
              })
            : buttonStyles
        }
        {...props}
      >
        <Typography
          sx={{
            width: "100%",
            alignSelf: "flex-start",
            textAlign: "left",
            alignItems: "center",
            fontWeight: 500,
            letterSpacing: 0.1,
          }}
        >
          {children}
        </Typography>
      </Button>
    </Link>
  );
};

const UpdateOrganizationPage: FC<unknown> = () => {
  const [organizationAddressWarning, setOrganizationAddressWarning] =
    useState<boolean>(false);

  const pages: Array<{
    page: string;
    displayName: string;
    icon: React.ReactElement;
    extra?: React.ReactNode;
  }> = [
    { page: "general", displayName: "General Settings", icon: <Settings /> },
    {
      page: "subscription",
      displayName: "Manage Subscription",
      icon: <AccountBalanceWallet />,
    },
    {
      page: "addresses",
      displayName: "Organization Addresses",
      icon: <CorporateFareRounded />,
      extra: (
        <Badge
          badgeContent={organizationAddressWarning ? 1 : 0}
          color="error"
          sx={{ ml: 2.5 }}
        />
      ),
    },
    {
      page: "contact",
      displayName: "Organization Contact Info",
      icon: <ContactPhone />,
    },
  ];
  const { success } = useAlert();
  const navigate = useNavigate();
  const {
    organization,
    setOrganization,
    refreshOrganizations,
    organizationBillingState,
  } = useGlobalOrganizationContext();

  const [refreshState, setRefreshState] = useState<number>(0);

  useEffect(() => {
    eventEmitter.on("organization:refresh", () => {
      setRefreshState((prev) => prev + 1);
    });

    return () => {
      eventEmitter.off("organization:refresh");
    };
  }, []);

  useEffect(() => {
    if (!organization) {
      setOrganizationAddressWarning(false);
      return;
    }
    if (!organizationBillingState || !organizationBillingState.stripe_id) {
      setOrganizationAddressWarning(false);
      return;
    }

    AddressAPI.getOrganizationAddressEntriesByLabel(
      organization.id,
      OrganizationAddressLabels.BILLING,
    ).then((result) => {
      if (result.length == 0) {
        setOrganizationAddressWarning(true);
      } else {
        setOrganizationAddressWarning(false);
      }
    });
  }, [organization, organizationBillingState, refreshState]);

  if (!organization) {
    return <NotFound />;
  }

  const UpdateOrganizationContextValue: OrganizationContextProps = {
    organization: organization,
    onOrganizationDeleted: () => {
      refreshOrganizations().then(() => {
        navigate(PATHS.USER_MANAGE_ORGANIZATIONS.link);
      });
    },
    setOrganization: setOrganization,
    refreshOrganizations: refreshOrganizations,
    onSubmit: async (organization: Organization) => {
      const updatedOrganization = await Organizations.update(organization);
      refreshOrganizations();
      setOrganization(updatedOrganization);
      success("Successfully updated organization");
    },
    disabled: false,
    viewSubscriptions: true,
    buttonAlignment: undefined,
  };

  //if (organization) {
  return (
    <PageContainer variant="full">
      <PageHeader title="Manage Organization" />
      <PageContent>
        <Container maxWidth="xl" sx={{ px: 2 }}>
          <Box display="flex" sx={{ flexDirection: "row" }}>
            <Paper
              elevation={0}
              sx={{
                minWidth: 350,
                position: "sticky",
                top: 0,
                alignSelf: "flex-start",
                zIndex: 2,
                backgroundColor: "#fff",
                p: 0,
              }}
            >
              <List
                sx={{
                  minWidth: 350,
                  flexDirection: "column",
                  backgroundColor: "#fff",
                  p: 0,
                  m: 0,
                }}
              >
                {pages.map((page) => (
                  <React.Fragment key={page.page}>
                    <MenuButton page={page.page} startIcon={page.icon}>
                      <Box
                        display="flex"
                        flexDirection="row"
                        alignItems="center"
                      >
                        <Typography>{page.displayName}</Typography>
                        {page.extra}
                      </Box>
                    </MenuButton>
                  </React.Fragment>
                ))}
              </List>
            </Paper>

            <Box flexGrow={1} sx={{ px: 5, overflow: "scroll" }}>
              <UpdateOrganizationContext.Provider
                value={UpdateOrganizationContextValue}
              >
                <Outlet />
              </UpdateOrganizationContext.Provider>
            </Box>
          </Box>
          {/*<UpdateOrganization
              organization={organization}
              onSubmit={async (organization: Organization) => {
                const updatedOrganization =
                  await Organizations.update(organization);
                refreshOrganizations();
                setOrganization(updatedOrganization);
                success("Successfully updated organization");
              }}
              onOrganizationDeleted={() => {
                refreshOrganizations().then(() => {
                  navigate(PATHS.USER_MANAGE_ORGANIZATIONS.link);
                });
              }}
              isCreatingOrganization={false}
              viewSubscriptions={true}
            />*/}
        </Container>
      </PageContent>
    </PageContainer>
  );
  //}
  //return <NotFound />;
};

export default UpdateOrganizationPage;
